// Pure helpers for the routing switches panel. They mirror the OMS rule in
// dealerapp/d2c/hold.area_rule, most specific first:
//   country off > city off > city on > region off > region on-list >
//   a city on-list narrowing its own region > everywhere.
// Empty on-lists mean everywhere. The server's env switches sit above all of it.

export const CHOICE_DEFAULT = "default";
export const CHOICE_ON = "on";
export const CHOICE_OFF = "off";

const MAX_NAMES = 5;

// Draft key -> PUT field.
const LIST_FIELDS = {
  onRegions: "enabled_region_ids",
  offRegions: "disabled_region_ids",
  onCities: "enabled_city_ids",
  offCities: "disabled_city_ids",
};
const FLAG_FIELDS = ["routing_enabled", "auto_enrol"];

// UUIDs compare case-insensitively: every id is lower-cased on the way in.
const norm = (id) => String(id).toLowerCase();

const sortedUnique = (ids) => [...new Set((ids || []).filter(Boolean).map(norm))].sort();

const sameIds = (left, right) =>
  left.length === right.length && left.every((id, index) => id === right[index]);

const regionIdsOf = (list) => sortedUnique((list || []).map((region) => region.region_id));
const cityIdsOf = (list) => sortedUnique((list || []).map((city) => city.city_id));

// One country row from GET switches as an editable draft. A missing row or
// field means the OMS model defaults: routing on, auto-enrol off, lists empty
// (on everywhere, still under the server's master switch).
export const toDraft = (row) => ({
  routing_enabled: row?.routing_enabled ?? true,
  auto_enrol: Boolean(row?.auto_enrol),
  onRegions: regionIdsOf(row?.enabled_regions),
  offRegions: regionIdsOf(row?.disabled_regions),
  onCities: cityIdsOf(row?.enabled_cities),
  offCities: cityIdsOf(row?.disabled_cities),
});

// The PUT body: the country plus only the fields that differ, or null when
// nothing changed.
export const buildSwitchPayload = (country, baseline, draft) => {
  const payload = {};
  FLAG_FIELDS.forEach((flag) => {
    if (Boolean(baseline[flag]) !== Boolean(draft[flag])) payload[flag] = Boolean(draft[flag]);
  });
  Object.entries(LIST_FIELDS).forEach(([key, field]) => {
    const next = sortedUnique(draft[key]);
    if (!sameIds(sortedUnique(baseline[key]), next)) payload[field] = next;
  });
  return Object.keys(payload).length ? { country, ...payload } : null;
};

const FIELD_LABELS = {
  routing_enabled: "the country switch",
  auto_enrol: "auto-enrol",
  enabled_region_ids: "regions set On",
  disabled_region_ids: "regions set Off",
  enabled_city_ids: "cities set On",
  disabled_city_ids: "cities set Off",
};

// Plain names for the fields a payload changes.
export const changedLabels = (payload) =>
  Object.keys(FIELD_LABELS)
    .filter((field) => payload && field in payload)
    .map((field) => FIELD_LABELS[field]);

// True when two drafts hold the same switches.
export const sameDraft = (left, right) =>
  Boolean(left.routing_enabled) === Boolean(right.routing_enabled) &&
  Boolean(left.auto_enrol) === Boolean(right.auto_enrol) &&
  Object.keys(LIST_FIELDS).every((key) => sameIds(sortedUnique(left[key]), sortedUnique(right[key])));

const choiceFrom = (id, onList, offList) => {
  const key = norm(id);
  if (offList.some((value) => norm(value) === key)) return CHOICE_OFF;
  if (onList.some((value) => norm(value) === key)) return CHOICE_ON;
  return CHOICE_DEFAULT;
};

export const regionChoice = (draft, regionId) => choiceFrom(regionId, draft.onRegions, draft.offRegions);
export const cityChoice = (draft, cityId) => choiceFrom(cityId, draft.onCities, draft.offCities);

const withChoice = (onList, offList, id, choice) => {
  const key = norm(id);
  const on = onList.filter((value) => norm(value) !== key);
  const off = offList.filter((value) => norm(value) !== key);
  if (choice === CHOICE_ON) on.push(key);
  if (choice === CHOICE_OFF) off.push(key);
  return [sortedUnique(on), sortedUnique(off)];
};

export const setRegionChoice = (draft, regionId, choice) => {
  const [onRegions, offRegions] = withChoice(draft.onRegions, draft.offRegions, regionId, choice);
  return { ...draft, onRegions, offRegions };
};

export const setCityChoice = (draft, cityId, choice) => {
  const [onCities, offCities] = withChoice(draft.onCities, draft.offCities, cityId, choice);
  return { ...draft, onCities, offCities };
};

// Whether an order in this region and city would be offered to dealers.
// `region` is a listing entry ({region_id, cities}); `cityId` may be null.
export const areaAllowed = (draft, region, cityId) => {
  if (!draft.routing_enabled) return false;
  const city = cityId == null ? null : norm(cityId);
  const regionId = region ? norm(region.region_id) : null;
  const onCities = sortedUnique(draft.onCities);
  const offCities = sortedUnique(draft.offCities);
  const onRegions = sortedUnique(draft.onRegions);
  const offRegions = sortedUnique(draft.offRegions);
  if (city !== null && offCities.includes(city)) return false;
  if (city !== null && onCities.includes(city)) return true;
  if (regionId !== null && offRegions.includes(regionId)) return false;
  if (onRegions.length && !onRegions.includes(regionId)) return false;
  if (!onCities.length) return true;
  if (regionId === null) return false;
  const regionCities = new Set((region.cities || []).map((entry) => norm(entry.city_id)));
  return !onCities.some((id) => regionCities.has(id));
};

// A region's effective state under the draft: "on" (all of it), "off" (none of
// it) or "some" (some cities), with the cities on each side.
export const regionEffect = (draft, region) => {
  const cities = region?.cities || [];
  if (!cities.length) {
    return { state: areaAllowed(draft, region, null) ? "on" : "off", onCities: [], offCities: [] };
  }
  const onCities = [];
  const offCities = [];
  cities.forEach((city) => (areaAllowed(draft, region, city.city_id) ? onCities : offCities).push(city));
  const state = !offCities.length ? "on" : !onCities.length ? "off" : "some";
  return { state, onCities, offCities };
};

// Stored ids the live geography no longer lists (a soft-deleted region or
// district). The PUT refuses unknown ids, so the screen offers to remove them.
// Each entry: {kind: "region"|"city", id, choice: "on"|"off"}.
export const unknownIds = (draft, regions) => {
  const listing = regions || [];
  const knownRegions = new Set(listing.map((region) => norm(region.region_id)));
  const knownCities = new Set(
    listing.flatMap((region) => (region.cities || []).map((city) => norm(city.city_id))),
  );
  const pick = (kind, known, ids, choice) =>
    ids.filter((id) => !known.has(norm(id))).map((id) => ({ kind, id, choice }));
  return [
    ...pick("region", knownRegions, draft.onRegions, CHOICE_ON),
    ...pick("region", knownRegions, draft.offRegions, CHOICE_OFF),
    ...pick("city", knownCities, draft.onCities, CHOICE_ON),
    ...pick("city", knownCities, draft.offCities, CHOICE_OFF),
  ];
};

// Drop one unknown id from whichever list holds it.
export const removeUnknown = (draft, entry) =>
  entry.kind === "region"
    ? setRegionChoice(draft, entry.id, CHOICE_DEFAULT)
    : setCityChoice(draft, entry.id, CHOICE_DEFAULT);

const names = (list) => {
  const all = list.map((entry) => entry.name || "Unnamed");
  if (all.length <= MAX_NAMES) return all.join(", ");
  return `${all.slice(0, MAX_NAMES).join(", ")} and ${all.length - MAX_NAMES} more`;
};

// The effective result in plain words, e.g.
// "Dealer routing is ON in: West except Mumbai; Delhi. OFF everywhere else."
// kind: "off", "everywhere" or "limited". `verb` lets the dialog say "will be".
export const describeArea = (draft, regions, country, verb = "is") => {
  const lead = `Dealer routing ${verb}`;
  if (!draft.routing_enabled) return { kind: "off", text: `${lead} OFF everywhere in ${country}.` };
  const effects = (regions || []).map((region) => ({ region, ...regionEffect(draft, region) }));
  if (effects.every((entry) => entry.state === "on")) {
    return { kind: "everywhere", text: `${lead} ON everywhere in ${country}.` };
  }
  const parts = effects
    .filter((entry) => entry.state !== "off")
    .map((entry) => {
      const name = entry.region.name || "Unnamed region";
      if (entry.state === "on") return name;
      // Name whichever side is shorter: "West except Mumbai" or "Delhi".
      return entry.offCities.length <= entry.onCities.length
        ? `${name} except ${names(entry.offCities)}`
        : names(entry.onCities);
    });
  if (!parts.length) return { kind: "off", text: `${lead} OFF everywhere in ${country}.` };
  return { kind: "limited", text: `${lead} ON in: ${parts.join("; ")}. OFF everywhere else.` };
};

// The sentences the confirmation dialog shows before a save.
export const confirmationLines = (country, baseline, draft, regions) => {
  const payload = buildSwitchPayload(country, baseline, draft);
  const lines = [];
  const areaChanged = changedLabels(payload).some((label) => label !== "auto-enrol");
  if (areaChanged) {
    lines.push(`Now: ${describeArea(baseline, regions, country).text}`);
    const after = describeArea(draft, regions, country, "will be");
    lines.push(`After saving: ${after.text}`);
    if (after.kind !== "everywhere") {
      lines.push("Orders waiting in areas that are off go back to central within a minute.");
    }
    lines.push("Offers already sent to dealers, and orders a dealer has accepted, carry on.");
  }
  if (payload && "auto_enrol" in payload) {
    lines.push(
      payload.auto_enrol
        ? "Auto-enrol will be ON: stores in switched-on areas join D2C by themselves within 5 minutes."
        : "Auto-enrol will be OFF: no store joins by itself. Stores already in D2C stay in.",
    );
  }
  return lines;
};
